import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Plus, Film, DollarSign, Eye, Download, Gift, Loader2, Video, ArrowUpRight,
  CheckCircle2, CircleDashed, HardDrive, Sparkles, Clock, AlertTriangle, Wallet,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { PricingDialog, fmtSize } from "@/components/PricingDialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const money = (cents, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: (currency || "usd").toUpperCase() }).format((cents || 0) / 100);

function StatPill({ icon: Icon, value, label }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-4 w-4 text-[#00F0FF]" />
      <span className="text-white font-semibold">{value}</span>
      <span className="text-white/40 text-xs">{label}</span>
    </div>
  );
}

export default function Dashboard() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [account, setAccount] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [withdrawing, setWithdrawing] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", event_date: "", location: "", complimentary_downloads: 5,
  });

  const loadAccount = useCallback(async () => {
    try {
      const { data } = await api.get("/me/account");
      setAccount(data);
    } catch { /* ignore */ }
  }, []);

  const loadWallet = useCallback(async () => {
    try {
      const { data } = await api.get("/payouts/wallet");
      setWallet(data);
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    load();
    loadAccount();
    loadWallet();
  }, [load, loadAccount, loadWallet]);

  const withdrawNow = async () => {
    setWithdrawing(true);
    try {
      const { data } = await api.post("/payouts/withdraw", {});
      toast.success(`Withdrawal of ${money(data.amount_cents, wallet?.stripe?.currency)} initiated 💸`);
      await loadWallet();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setWithdrawing(false);
    }
  };

  // Handle return from Stripe subscription checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    window.history.replaceState(null, "", "/dashboard");
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          toast.success(`Upgraded to ${data.tier?.toUpperCase()} plan 🎉`);
          await Promise.all([loadAccount(), checkAuth()]);
          return;
        }
      } catch { /* ignore */ }
      if (tries < 8) setTimeout(poll, 1800);
    };
    poll();
  }, [loadAccount, checkAuth]);

  const handleNewVault = () => {
    if (account?.vault_limit != null && events.length >= account.vault_limit) {
      toast.error(`Your ${account.tier_name} plan allows ${account.vault_limit} vaults. Upgrade to add more.`);
      setShowPricing(true);
      return;
    }
    setOpen(true);
  };

  const createEvent = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/events", {
        ...form,
        complimentary_downloads: Number(form.complimentary_downloads),
      });
      toast.success("Event vault created");
      setOpen(false);
      setForm({ title: "", description: "", event_date: "", location: "", complimentary_downloads: 5 });
      navigate(`/events/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Header />
      <main className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#00F0FF]">Creator studio</p>
            <h1 className="font-display text-3xl md:text-4xl font-bold mt-2">
              Hey {user?.name?.split(" ")[0] || "there"} 👋
            </h1>
            <p className="text-white/50 mt-1">Manage your vaults and track revenue.</p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/dashboard/payouts"
              data-testid="dashboard-payouts-link"
              className="inline-flex items-center gap-2 border border-white/15 rounded-full px-5 py-3 text-sm font-medium hover:bg-white/10 transition-colors"
            >
              <Wallet className="h-4 w-4 text-[#00F0FF]" /> Payouts
            </Link>
            <Dialog open={open} onOpenChange={setOpen}>
              <button
                onClick={handleNewVault}
                data-testid="create-event-btn"
                className="inline-flex items-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-3 hover:scale-105 transition-transform duration-300 cyan-glow"
              >
                <Plus className="h-4 w-4" /> New Vault
              </button>
            <DialogContent className="bg-[#0d0d0d] border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">Create Vault</DialogTitle>
              </DialogHeader>
              <form onSubmit={createEvent} className="space-y-4 mt-2">
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/60">Event title</label>
                  <input
                    data-testid="event-title-input"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Sarah & Tom's Wedding"
                    className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/60">Description</label>
                  <textarea
                    data-testid="event-desc-input"
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="A brief note for your client…"
                    className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF] resize-none"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/60">Date</label>
                    <input
                      data-testid="event-date-input"
                      type="date"
                      value={form.event_date}
                      onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                      className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-white/60">Location</label>
                    <input
                      data-testid="event-location-input"
                      value={form.location}
                      onChange={(e) => setForm({ ...form, location: e.target.value })}
                      placeholder="Napa Valley"
                      className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-white/60">Complimentary downloads</label>
                  <input
                    data-testid="event-comp-input"
                    type="number"
                    min={0}
                    value={form.complimentary_downloads}
                    onChange={(e) => setForm({ ...form, complimentary_downloads: e.target.value })}
                    className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#00F0FF]"
                  />
                  <p className="text-xs text-white/40 mt-1">Free downloads your client can spend on any clip.</p>
                </div>
                <DialogFooter>
                  <button
                    data-testid="event-create-submit"
                    disabled={saving}
                    className="w-full bg-[#00F0FF] text-black font-semibold rounded-full py-3 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Create Vault
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Available to withdraw nudge */}
        {wallet && wallet.stripe?.connected && wallet.stripe?.onboarded && (wallet.stripe?.available_cents || 0) > 0 && (
          <div
            data-testid="withdraw-nudge"
            className="mb-6 rounded-2xl border border-[#FFDD03]/40 bg-gradient-to-r from-[#FFDD03]/10 to-transparent px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <span className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#FFDD03]/15 text-[#FFDD03] shrink-0">
                <Wallet className="h-5 w-5" />
              </span>
              <span className="text-sm">
                <span className="text-white/60">Available to withdraw: </span>
                <span data-testid="withdraw-nudge-amount" className="font-display text-lg font-bold text-[#FFDD03]">
                  {money(wallet.stripe.available_cents, wallet.stripe.currency)}
                </span>
                <span className="block text-xs text-white/40">Your clip-sale earnings are ready to cash out.</span>
              </span>
            </span>
            <button
              onClick={withdrawNow}
              disabled={withdrawing || !wallet.stripe?.payouts_enabled}
              data-testid="withdraw-nudge-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#FFDD03] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:hover:scale-100"
            >
              {withdrawing ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
              Withdraw now
            </button>
          </div>
        )}

        {/* Earned-but-not-connected nudge */}
        {wallet && (wallet.lifetime?.net_cents || 0) > 0 && !(wallet.stripe?.connected && wallet.stripe?.onboarded) && (
          <div
            data-testid="connect-nudge"
            className="mb-6 rounded-2xl border border-[#00F0FF]/40 bg-gradient-to-r from-[#00F0FF]/10 to-transparent px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <span className="flex items-center gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-xl bg-[#00F0FF]/15 text-[#00F0FF] shrink-0">
                <DollarSign className="h-5 w-5" />
              </span>
              <span className="text-sm">
                <span className="text-white/60">You've earned </span>
                <span className="font-display text-lg font-bold text-emerald-400">
                  {money(wallet.lifetime.net_cents)}
                </span>
                <span className="block text-xs text-white/40">Connect Stripe to cash out your clip sales.</span>
              </span>
            </span>
            <Link
              to="/dashboard/payouts"
              data-testid="connect-nudge-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-[#00F0FF] text-black font-semibold rounded-full px-6 py-2.5 text-sm hover:scale-105 transition-transform duration-300"
            >
              <Wallet className="h-4 w-4" /> Set up payouts
            </Link>
          </div>
        )}

        {/* Plan + storage */}
        {account && (
          <div className="mb-6 rounded-2xl border border-white/10 bg-[#0d0d0d] p-5 flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="inline-flex items-center gap-2 text-sm">
                  <HardDrive className="h-4 w-4 text-[#00F0FF]" />
                  <span className="font-semibold">{account.tier_name} plan</span>
                  <span className="text-white/40">· {fmtSize(account.storage_used)} / {fmtSize(account.storage_limit)}</span>
                </span>
                <span className={`text-xs ${account.over_limit ? "text-[#FFDD03]" : "text-white/40"}`}>
                  {Math.min(100, Math.round((account.storage_used / account.storage_limit) * 100))}%
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-white/10 overflow-hidden" data-testid="storage-bar">
                <div
                  className={`h-full rounded-full transition-all ${account.over_limit ? "bg-[#FFDD03]" : "bg-[#00F0FF]"}`}
                  style={{ width: `${Math.min(100, (account.storage_used / account.storage_limit) * 100)}%` }}
                />
              </div>
            </div>
            <button
              onClick={() => setShowPricing(true)}
              data-testid="upgrade-btn"
              className="shrink-0 inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
            >
              <Sparkles className="h-4 w-4 text-[#00F0FF]" /> {account.tier === "starter" ? "Upgrade plan" : "Manage plan"}
            </button>
          </div>
        )}

        {/* Over-limit banner */}
        {account?.over_limit && (
          <div data-testid="overlimit-banner" className="mb-4 rounded-xl border border-[#FFDD03]/40 bg-[#FFDD03]/10 px-5 py-3 flex items-center justify-between gap-4">
            <span className="flex items-center gap-2 text-sm text-[#FFDD03]">
              <AlertTriangle className="h-4 w-4" /> You're over your storage limit. Uploads still work, but upgrade to keep things smooth.
            </span>
            <button onClick={() => setShowPricing(true)} className="shrink-0 text-sm font-semibold text-black bg-[#FFDD03] rounded-full px-4 py-1.5">Upgrade</button>
          </div>
        )}

        {/* Expiry banner (Basic tier) */}
        {account?.expiring_vaults?.length > 0 && (
          <div data-testid="expiry-banner" className="mb-6 rounded-xl border border-[#FFDD03]/40 bg-[#FFDD03]/10 px-5 py-3 flex items-center justify-between gap-4">
            <span className="flex items-center gap-2 text-sm text-[#FFDD03]">
              <Clock className="h-4 w-4" />
              {account.expiring_vaults.length === 1
                ? `"${account.expiring_vaults[0].title}" expires in ${account.expiring_vaults[0].days_left} day(s). Basic vaults are deleted after ${account.expiry_days} days.`
                : `${account.expiring_vaults.length} vaults are expiring soon. Basic vaults are deleted after ${account.expiry_days} days.`}
            </span>
            <button onClick={() => setShowPricing(true)} className="shrink-0 text-sm font-semibold text-black bg-[#FFDD03] rounded-full px-4 py-1.5">Keep them</button>
          </div>
        )}

        {loading ? (
          <div className="grid place-items-center py-32">
            <Loader2 className="h-8 w-8 animate-spin text-[#00F0FF]" />
          </div>
        ) : events.length === 0 ? (
          <div className="rounded-3xl border border-dashed border-white/15 py-24 text-center">
            <span className="grid place-items-center h-16 w-16 rounded-2xl bg-[#00F0FF]/10 text-[#00F0FF] mx-auto">
              <Film className="h-8 w-8" />
            </span>
            <h3 className="font-display text-xl font-semibold mt-6">No vaults yet</h3>
            <p className="text-white/50 mt-2">Create your first vault to start selling footage.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((ev) => (
              <Link
                key={ev.id}
                to={`/events/${ev.id}`}
                data-testid={`event-card-${ev.id}`}
                className="group rounded-2xl border border-white/10 bg-[#0d0d0d] overflow-hidden hover:border-[#00F0FF]/40 transition-colors"
              >
                <div className="relative aspect-video overflow-hidden bg-gradient-to-br from-[#141414] to-black">
                  {ev.cover_image_path ? (
                    <img
                      src={`${BACKEND}/api/files/${ev.cover_image_path}`}
                      alt=""
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-white/20">
                      <Video className="h-10 w-10" />
                    </div>
                  )}
                  <div className="absolute top-3 left-3">
                    {ev.published ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md px-3 py-1 text-xs text-emerald-400 border border-emerald-400/20">
                        <CheckCircle2 className="h-3 w-3" /> Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-black/60 backdrop-blur-md px-3 py-1 text-xs text-white/60 border border-white/10">
                        <CircleDashed className="h-3 w-3" /> Draft
                      </span>
                    )}
                  </div>
                  <ArrowUpRight className="absolute top-3 right-3 h-5 w-5 text-white/0 group-hover:text-[#00F0FF] transition-colors" />
                  {ev.expiry?.expiring_soon && (
                    <span className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-[#FFDD03] text-black px-3 py-1 text-xs font-semibold">
                      <Clock className="h-3 w-3" /> Expires in {ev.expiry.days_left}d
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold truncate">{ev.title}</h3>
                  <p className="text-xs text-white/40 mt-0.5">
                    {ev.analytics.video_count} clips{ev.location ? ` · ${ev.location}` : ""}
                  </p>
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <span className="inline-flex items-center gap-1.5 text-emerald-400 font-semibold">
                      <DollarSign className="h-4 w-4" />${ev.analytics.total_revenue.toFixed(2)}
                    </span>
                    <div className="flex items-center gap-3 text-white/50 text-xs">
                      <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{ev.analytics.total_views}</span>
                      <span className="inline-flex items-center gap-1"><Download className="h-3.5 w-3.5" />{ev.analytics.total_downloads}</span>
                      <span className="inline-flex items-center gap-1"><Gift className="h-3.5 w-3.5" />{ev.analytics.complimentary_redeemed}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>

      <footer className="border-t border-white/10 py-8 mt-8">
        <div className="max-w-7xl mx-auto px-5 md:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/40">
          <Link to="/" className="font-display font-semibold text-white">Narrative<span className="text-[#00F0FF]">Vault</span></Link>
          <div className="flex items-center gap-6">
            <Link to="/faq" className="hover:text-white transition-colors">FAQ & Pricing</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/copyright" className="hover:text-white transition-colors">Copyright</Link>
            <span>© {new Date().getFullYear()} — by WTN Productions</span>
          </div>
        </div>
      </footer>

      <PricingDialog
        open={showPricing}
        onOpenChange={setShowPricing}
        tiers={account?.tiers || []}
        currentTier={account?.tier}
      />
    </div>
  );
}
